import { useState, useMemo } from 'react';
import { Box, Typography, Button, InputAdornment, Icon, IconButton } from '@mui/material';
import MobileTextField from './components/MobileTextField';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/store';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import { forgotPassword, resendSignUp, forgotPasswordSubmit } from 'app/store/authSlice';
import MobileAuthLayout from './components/MobileAuthLayout';
import PasswordValidationList from './components/PasswordValidationList';
import { REPLAY_CHROME_CLASS, sentryMask } from 'sentryReplayPrivacy';

const useValidationSchema = (codeSent: boolean, codeVerified: boolean, t: (key: string) => string) => {
  if (codeVerified) {
    return yup.object().shape({
      code: yup.string().required(t('mobileValidationEnterCode')),
      password: yup
        .string()
        .required(t('mobileValidationEnterPassword'))
        .min(8, t('mobileValidationPasswordMinLength'))
        .matches(/[A-Z]/, t('mobileValidationPasswordUppercase'))
        .matches(/\d/, t('mobileValidationPasswordNumber'))
        .matches(/[!@#$%^&*(),.?":{}|<>]/, t('mobileValidationPasswordSpecial')),
      confirmPassword: yup
        .string()
        .required(t('mobileValidationEnterPassword'))
        .oneOf([yup.ref('password')], t('mobileValidationPasswordsMatch')),
    });
  }
  if (codeSent) {
    return yup.object().shape({
      code: yup.string().required(t('mobileValidationEnterCode')),
    });
  }
  return yup.object().shape({
    username: yup
      .string()
      .required(t('mobileValidationEnterUsername'))
      .matches(/^\S+$/, t('mobileValidationUsernameNoSpaces')),
  });
};

const defaultValues = {
  username: '',
  code: '',
  password: '',
  confirmPassword: '',
};

function MobileForgotPassword(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const { control, formState, handleSubmit, setError, watch } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(useValidationSchema(codeSent, !isEmpty(verificationCode), t)),
  });

  const { isValid, dirtyFields, errors } = formState;
  const watchUsername = watch('username');
  const watchPassword = watch('password');
  const watchConfirmPassword = watch('confirmPassword');

  // Password validation functions - memoized to prevent unnecessary re-renders
  const validationState = useMemo(() => {
    const hasCapitalLetter = /[A-Z]/.test(watchPassword);
    const hasNumber = /\d/.test(watchPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(watchPassword);
    const passwordsMatch = watchPassword === watchConfirmPassword && watchPassword !== '';

    return {
      hasCapitalLetter,
      hasNumber,
      hasSpecialChar,
      passwordsMatch,
      allRequirementsMet: hasCapitalLetter && hasNumber && hasSpecialChar && passwordsMatch,
    };
  }, [watchPassword, watchConfirmPassword]);

  const validationItems = useMemo(
    () => [
      { text: t('mobileNewPasswordValidationCapital'), isValid: validationState.hasCapitalLetter },
      { text: t('mobileNewPasswordValidationNumber'), isValid: validationState.hasNumber },
      { text: t('mobileNewPasswordValidationSpecial'), isValid: validationState.hasSpecialChar },
      { text: t('mobileNewPasswordValidationMatch'), isValid: validationState.passwordsMatch },
    ],
    [validationState, t]
  );

  const handleBackClick = () => {
    navigate('/login');
  };

  const onResendCode = async () => {
    if (isResending) return;
    setIsResending(true);
    const action = await dispatch(forgotPassword({ username: watchUsername }));
    setIsResending(false);
    if (action.meta.requestStatus === 'fulfilled') {
      enqueueSnackbar(t('mobileSnackbarCheckEmail'), { variant: 'info' });
    } else {
      const payload = action.payload as { message?: string } | undefined;
      setError('username', {
        type: 'manual',
        message: payload?.message || t('mobilePasswordChangeError'),
      });
    }
  };

  const verifyCode = async (model: { username: string; code: string }) => {
    setVerificationCode(model.code);
  };

  const onSubmit = async (model: {
    username: string;
    code: string;
    password?: string;
    confirmPassword?: string;
  }) => {
    if (!isEmpty(verificationCode)) {
      // Submit the new password
      if (!validationState.allRequirementsMet) return;
      setIsLoading(true);
      try {
        const action = await dispatch(
          forgotPasswordSubmit({
            username: model.username.trim(),
            code: verificationCode.trim(),
            new_password: model.password!,
          })
        );

        if (action.meta.requestStatus === 'fulfilled') {
          enqueueSnackbar(t('mobileSnackbarPasswordResetSuccess'), { variant: 'success' });
          navigate('/login');
        } else {
          const payload = action.payload as { name?: string; message?: string } | undefined;
          enqueueSnackbar(payload?.message || t('mobilePasswordChangeError'), { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar(t('mobilePasswordChangeError'), { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    } else if (codeSent) {
      await verifyCode(model);
    } else {
      setIsLoading(true);
      const action = await dispatch(forgotPassword(model));

      if (action.meta.requestStatus === 'fulfilled') {
        setCodeSent(true);
        enqueueSnackbar(t('mobileSnackbarCheckEmail'), { variant: 'info' });
        setIsLoading(false);
      } else {
        // Check for unverified user error
        const payload = action.payload as { name?: string; message?: string } | undefined;
        if (
          payload?.name === 'InvalidParameterException' &&
          payload?.message?.includes('no registered/verified email')
        ) {
          // User is not verified, send new verification code and redirect
          const resendAction = await dispatch(resendSignUp({ username: model.username }));
          if (resendAction.meta.requestStatus === 'fulfilled') {
            navigate(`/verify-user/${model.username}`);
            enqueueSnackbar(t('mobileSnackbarVerifyAccountFirst'), { variant: 'warning' });
          } else {
            const resendPayload = resendAction.payload as { message?: string } | undefined;
            setError('username', {
              type: 'manual',
              message: resendPayload?.message || t('mobilePasswordChangeError'),
            });
          }
          setIsLoading(false);
        } else {
          setError('username', {
            type: 'manual',
            message: payload?.message || t('mobilePasswordChangeError'),
          });
          setIsLoading(false);
        }
      }
    }
  };

  return (
    <MobileAuthLayout
      onBack={handleBackClick}
      footer={
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={
              !isEmpty(verificationCode)
                ? !validationState.allRequirementsMet || isLoading
                : isEmpty(dirtyFields) || !isValid || isLoading
            }
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '8px',
              py: 1.5,
              px: 4,
              minWidth: '300px',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.text.disabled,
              },
            })}
          >
            {isLoading
              ? t('mobileSending')
              : !isEmpty(verificationCode)
              ? t('mobileNewPasswordResetButton')
              : t('mobileNext')}
          </Button>
        </Box>
      }
    >
      {/* Replay mask: username and code inputs have no distinguishing attribute. */}
      <Box
        {...sentryMask}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontSize: 30, fontWeight: 700, lineHeight: '40px', mb: 2 }}>
          {!isEmpty(verificationCode)
            ? t('mobileNewPasswordTitle')
            : codeSent
            ? t('mobileEnterVerificationCodeTitle')
            : t('mobileForgotPasswordTitle')}
        </Typography>

        {isEmpty(verificationCode) && (
          <Typography
            variant="body1"
            className={REPLAY_CHROME_CLASS}
            sx={{ fontSize: 16, fontWeight: 274, lineHeight: '24px', mb: 2 }}
          >
            {codeSent ? t('mobileEnterVerificationCodeDescription') : t('mobileForgotPasswordDescription')}
          </Typography>
        )}

        {!codeSent && (
          <Box>
            <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 274 }}>
              {t('username')}
            </Typography>
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <MobileTextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  placeholder={t('mobileSignInEnterUsername')}
                  error={!!errors.username}
                  helperText={errors?.username?.message}
                  disabled={isLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: 'divider',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'divider',
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        )}

        {codeSent && isEmpty(verificationCode) && (
          <>
            <Box>
              <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 274 }}>
                {t('mobileVerificationCode')}
              </Typography>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <MobileTextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    placeholder={t('mobileEnterVerificationCode')}
                    error={!!errors.code}
                    helperText={errors?.code?.message}
                    disabled={isLoading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'divider',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'divider',
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Typography
                component="button"
                className={REPLAY_CHROME_CLASS}
                onClick={onResendCode}
                disabled={isResending}
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: 'primary.main',
                  textAlign: 'center',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: isResending ? 'not-allowed' : 'pointer',
                  opacity: isResending ? 0.6 : 1,
                  '&:hover': {
                    textDecoration: isResending ? 'none' : 'underline',
                  },
                }}
              >
                {isResending ? t('mobileSending') : t('mobileResendCode')}
              </Typography>
            </Box>
          </>
        )}

        {!isEmpty(verificationCode) && (
          <>
            {/* Password field */}
            <Box>
              <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 274 }}>
                {t('password')}
              </Typography>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <MobileTextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('mobileNewPasswordEnterPassword')}
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('togglePasswordVisibility')}
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="large"
                          >
                            <Icon className="text-20" color="action">
                              {showPassword ? 'visibility' : 'visibility_off'}
                            </Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'divider',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'divider',
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Re-enter Password field */}
            <Box>
              <Typography variant="body1" className={REPLAY_CHROME_CLASS} sx={{ mb: 1, fontWeight: 274 }}>
                {t('mobileNewPasswordReenterLabel')}
              </Typography>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <MobileTextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('mobileNewPasswordReenterPlaceholder')}
                    error={!!errors.confirmPassword}
                    helperText={errors?.confirmPassword?.message}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('mobileNewPasswordToggleConfirmVisibility')}
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            size="large"
                          >
                            <Icon className="text-20" color="action">
                              {showConfirmPassword ? 'visibility' : 'visibility_off'}
                            </Icon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '& fieldset': {
                          borderColor: 'divider',
                        },
                        '&:hover fieldset': {
                          borderColor: 'divider',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'divider',
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Password validation requirements */}
            <PasswordValidationList items={validationItems} />
          </>
        )}
      </Box>
    </MobileAuthLayout>
  );
}

export default MobileForgotPassword;
